// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 42-app-marketplace.md
// @greenlight-spec: 19-design-system.md
// Access tab: who can use the app (people + general access) above what the app
// itself may reach (integration grants).
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AD_CARD, AD_CARD_HD } from '@/components/greenlight/surfaces';
import { Button } from '@/components/ui/button';
import { AppAccessCard } from './AppAccessCard';
import { PermissionsTab } from './PermissionsTab';

/**
 * One tab for everything access-shaped (docs/28 §App detail): who can open and
 * manage the app, and what the app itself may reach. Filter chips on the
 * permissions card are only shown when the user can manage grants (same gate as
 * the Manage link).
 */
export function AccessTab({
  appId,
  showRbac,
  ownerEmail,
  ownerName,
  accessRestricted,
}: {
  appId: string;
  showRbac: boolean;
  ownerEmail: string | null;
  ownerName?: string | null;
  accessRestricted: boolean;
}): React.JSX.Element {
  return (
    // Who can use the app comes first — it is the question an owner opens this
    // tab to answer. Integration permissions sit below: a different question
    // (what the app may reach) about a different subject.
    <div className="space-y-4" data-testid="access-tab">
      <div className={AD_CARD}>
        <div className={AD_CARD_HD}>
          <span className="text-sm font-semibold">Who can use this app</span>
        </div>
        <div className="p-5">
          <AppAccessCard
            appId={appId}
            ownerEmail={ownerEmail}
            ownerName={ownerName}
            accessRestricted={accessRestricted}
          />
        </div>
      </div>
      <div className={AD_CARD}>
        <div className={AD_CARD_HD}>
          <span className="text-sm font-semibold">Integration permissions</span>
          {/* Decisions live on the Integrations tab's Grants sub-view (IT-only),
              so only IT gets the Manage link — scoped to this app's grants. */}
          {showRbac && (
            <Button asChild variant="outline" size="sm">
              <Link to={`/integrations?view=grants&group=app&appId=${appId}`}>Manage</Link>
            </Button>
          )}
        </div>
        <div className="p-5">
          <PermissionsTab appId={appId} canManage={showRbac} />
        </div>
      </div>
    </div>
  );
}
